import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  Paper,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InfoIcon from "@mui/icons-material/Info";
import { useState, useCallback } from "react";
import LoadingButton from "../../../components/LoadingButton";
import TagsAutocomplete from "../../../components/TagsAutocomplete";
import { ALLOWED_DOCUMENT_EXTENSIONS, DOCUMENT_ACCEPT_TYPES, DOCUMENT_FILE_TYPES, DOCUMENT_TYPE_LABELS, MAX_DOCUMENT_SIZE_MB, PREMIUM_PDF_CREDIT_COST } from "../../../constants";

interface UploadDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  onUploadDocument: (file: File, tags: string[], folderId?: string, usePremiumPdf?: boolean) => Promise<void>;
  availableTags: string[];
  selectedFolderId?: string;
  loading?: boolean;
  companyId: string;
  getToken: () => Promise<string>;
  eventListener?: (event: { type: string; error?: string; message?: string }) => void;
}

export default function UploadDocumentDialog({
  open,
  onClose,
  onUploadDocument,
  availableTags,
  selectedFolderId,
  loading = false,
  companyId,
  getToken,
  eventListener,
}: UploadDocumentDialogProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [usePremiumPdf, setUsePremiumPdf] = useState(false);

  const handleClose = () => {
    setSelectedFile(null);
    setTags([]);
    setIsDragOver(false);
    setUsePremiumPdf(false);
    onClose();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelection(file);
    }
  };

  const validateFileType = (file: File) => {
    const fileName = file.name.toLowerCase();
    return ALLOWED_DOCUMENT_EXTENSIONS.some(type => fileName.endsWith(type));
  };

  const handleFileSelection = (file: File) => {
    if (!validateFileType(file)) {
      const supportedTypes = ALLOWED_DOCUMENT_EXTENSIONS
        .map(ext => DOCUMENT_TYPE_LABELS[ext as keyof typeof DOCUMENT_TYPE_LABELS] || ext.toUpperCase())
        .join(', ');
      eventListener?.({
        type: "SET_ERROR",
        error: `Please select a valid file type. Supported formats: ${supportedTypes}`,
      });
      return;
    }

    // Check file size
    const fileSizeMB = file.size / 1024 / 1024;
    if (fileSizeMB > MAX_DOCUMENT_SIZE_MB) {
      eventListener?.({
        type: "SET_ERROR",
        error: `File size exceeds ${MAX_DOCUMENT_SIZE_MB}MB limit. Please select a smaller file.`,
      });
      return;
    }

    setSelectedFile(file);
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!loading) {
      setIsDragOver(true);
    }
  }, [loading]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    if (loading) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      handleFileSelection(file);
    }
  }, [loading]);

  const handleUpload = async () => {
    if (selectedFile) {
      await onUploadDocument(selectedFile, tags, selectedFolderId, usePremiumPdf);
      handleClose();
    }
  };

  // Check if selected file is a PDF
  const isPdfFile = selectedFile?.name.toLowerCase().endsWith('.pdf') || false;

  // Check if selected file is an audio file (transcription uses different pricing)
  const isAudioFile = selectedFile ? DOCUMENT_FILE_TYPES.AUDIO.some(ext =>
    selectedFile.name.toLowerCase().endsWith(ext)
  ) : false;

  // Estimate credits based on file size
  // For text-heavy documents: ~4 characters per token, 1 credit per 1000 tokens
  // For audio files: transcription pricing varies, show as estimate
  const estimateCredits = (file: File): { min: number; max: number } => {
    const fileSizeBytes = file.size;
    const fileSizeKB = fileSizeBytes / 1024;

    if (isAudioFile) {
      // Audio files: rough estimate based on duration (can't know exact without processing)
      // Assume ~1MB per minute of audio, ~150 words per minute, ~200 tokens per minute
      const estimatedMinutes = fileSizeBytes / (1024 * 1024);
      const estimatedTokens = estimatedMinutes * 200;
      const credits = Math.max(1, Math.floor(estimatedTokens / 1000));
      return { min: credits, max: credits * 2 }; // Wide range for audio
    }

    // Text documents: estimate ~250 tokens per KB for text-heavy docs
    // PDFs with images will have fewer tokens, so we show a range
    const tokensPerKB = isPdfFile ? 150 : 250; // PDFs often have more overhead
    const estimatedTokens = fileSizeKB * tokensPerKB;
    const estimatedCredits = Math.max(1, Math.floor(estimatedTokens / 1000));

    // Show a range: 50% to 100% of estimate for PDFs (due to images/formatting variance)
    // For text files, narrower range
    if (isPdfFile) {
      return { min: Math.max(1, Math.floor(estimatedCredits * 0.3)), max: estimatedCredits };
    }
    return { min: Math.max(1, Math.floor(estimatedCredits * 0.7)), max: estimatedCredits };
  };

  const creditEstimate = selectedFile ? estimateCredits(selectedFile) : null;

  const isValid = selectedFile !== null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Upload Document
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 3 }}>
          Upload a document to your knowledge base. Supported formats: PDF, Word documents, Excel/CSV spreadsheets, text files (TXT, MD, JSON, HTML), and audio files for transcription. Maximum file size: {MAX_DOCUMENT_SIZE_MB}MB.
        </Typography>
        
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            mb: 2,
            border: '2px dashed',
            borderColor: isDragOver ? 'primary.main' : 'grey.300',
            backgroundColor: isDragOver ? 'primary.50' : 'transparent',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover',
            }
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            {isDragOver ? 'Drop your file here' : 'Drag and drop your file here'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            or
          </Typography>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
            disabled={loading}
            sx={{ mb: 2 }}
          >
            {selectedFile ? selectedFile.name : "Choose File"}
            <input
              type="file"
              accept={DOCUMENT_ACCEPT_TYPES}
              hidden
              onChange={handleFileChange}
            />
          </Button>
          {selectedFile && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                File size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
              {creditEstimate && (
                <Typography variant="body2" color="text.secondary">
                  Estimated cost: {creditEstimate.min === creditEstimate.max
                    ? `~${creditEstimate.min} credit${creditEstimate.min !== 1 ? 's' : ''}`
                    : `${creditEstimate.min}-${creditEstimate.max} credits`}
                  {usePremiumPdf && ` (+${PREMIUM_PDF_CREDIT_COST} for premium PDF)`}
                </Typography>
              )}
            </Box>
          )}
          {!selectedFile && (
            <Typography variant="caption" color="text.secondary" display="block">
              Supported formats: PDF, Word, Excel, CSV, text files, and audio files (max {MAX_DOCUMENT_SIZE_MB}MB)
            </Typography>
          )}
        </Paper>

        {isPdfFile && (
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={usePremiumPdf}
                  onChange={(e) => setUsePremiumPdf(e.target.checked)}
                  disabled={loading}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>
                    Premium PDF Upload (+{PREMIUM_PDF_CREDIT_COST} credits)
                  </Typography>
                  <Tooltip title="Uses advanced AI processing to extract better context from complex PDFs, including those with tables, images, and difficult layouts. Provides more accurate text extraction for improved document understanding.">
                    <InfoIcon fontSize="small" color="action" />
                  </Tooltip>
                </Box>
              }
            />
            {usePremiumPdf && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
                This will use {PREMIUM_PDF_CREDIT_COST} additional credits to process your PDF with enhanced context extraction using OpenRouter's multimodal capabilities.
              </Typography>
            )}
          </Box>
        )}

        <TagsAutocomplete
          value={tags}
          onChange={setTags}
          companyId={companyId}
          getToken={getToken}
          placeholder="Add tags to categorize this document"
          disabled={loading}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Chip
          label="~1 credit per page"
          size="small"
          variant="outlined"
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
            <LoadingButton
            variant="contained"
            onClick={handleUpload}
            loading={loading}
            disabled={!isValid}
          >
            Upload Document
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}